'use client';

import type { DrawSelectorItemType } from '../../../../lotteries/DrawSelector/DrawSelector';
import type { ISODateString, ParticipationState } from '../../../types';

type ParticipationDate = DrawSelectorItemType & { drawingDate: ISODateString };

/**
 * useParticipationDateStatus hook generates an array of date status items suitable for the DrawSelector,
 * representing the completed and upcoming participations.
 * When there is only one participation, it is selected and enabled.
 * Multiple participations with future participation will have the first future participation selected and enabled,
 * but in case where future participation is not present, the last participation is selected and enabled.
 *
 * @param {Participation[]} participations - List of participation objects to process.
 * @returns {ParticipationDate[]} List of participation date statuses, sorted and flagged for UI.
 */
function useParticipationDate(participations: { drawingId: string; drawingDate: ISODateString; participationState: ParticipationState }[]): ParticipationDate[] {
  const count = participations.length;
  if (count === 0) return [];

  const now = new Date();
  const items = participations.map(({ drawingId, drawingDate, participationState }) => ({
    drawingId,
    drawingDate,
    date: new Date(drawingDate),
    participationState,
  }));

  const firstFutureIndex = items.findIndex(item => item.date > now);
  const hasFutureParticipations = firstFutureIndex !== -1;

  return items.map(({ drawingId, drawingDate, date, participationState }, index) => ({
    drawingId,
    date,
    drawingDate,
    participationState,
    isDisabled: hasFutureParticipations && index > firstFutureIndex,
    isSelected: hasFutureParticipations ? index === firstFutureIndex : index === count - 1,
  }));
}

export { useParticipationDate };
export type { ParticipationDate };
